import axios from "axios";
import { signOut as firebaseSignOut } from "firebase/auth";


export default function useSchedulerActions({
  auth,
  API_BASE_URL,
  refresh,          // this replaces fetchAllEvents()
  setToast,
  setIsEditing,
  setIsModalOpen,
  selectedEvent,
  setSelectedEvent,
  navigate,
}) {

  // === File Upload ===
  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
      setToast({ message: "⚠️ Only CSV files are allowed!", type: "error" });
      e.target.value = "";
      return;
    }

    try {
      const token = await auth.currentUser.getIdToken(true);

      const formData = new FormData();
      formData.append("file", file);

      const endpoint =
        type === "work"
          ? `${API_BASE_URL}/api/work/upload/`
          : `${API_BASE_URL}/api/event/upload/`;

      const res = await fetch(endpoint, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
      });

      const data = await res.json();

      if (res.ok) {
        setToast({
          message: data.message || `✅ ${type} uploaded successfully`,
          type: "success",
        });

        refresh(); // 🔥 replaces fetchAllEvents()
      } else {
        setToast({
          message: `⚠️ ${type} upload failed: ${data.error || res.statusText}`,
          type: "error",
        });
      }
    } catch (err) {
      setToast({ message: `⚠️ ${type} upload failed: ${err.message}`, type: "error" });
    } finally {
      e.target.value = "";
    }
  };

  // === Delete All ===
  const handleDeleteAll = async (type) => {
    try {
      const token = await auth.currentUser.getIdToken(true);

      const endpoint =
        type === "work"
          ? `${API_BASE_URL}/api/work/delete_all/`
          : `${API_BASE_URL}/api/event/delete_all/`;

      const res = await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });

      setToast({
        message: res.data.message || `✅ All ${type} events deleted.`,
        type: "success",
      });

      refresh();
    } catch (err) {
      setToast({
        message: `⚠️ Delete failed: ${err.response?.data?.error || err.message}`,
        type: "error",
      });
    } finally {

    }
  };

  const handleSaveWorkEdit = async (workToSave, closeEditModal, closeViewModal) => {
    if (!workToSave) return;

    const missingFields = [];

    if (!workToSave.description?.trim())
      missingFields.push("Event Description");

    if (!workToSave.date_and_time)
      missingFields.push("Date & Time");

    if (!workToSave.primary_technician?.trim())
      missingFields.push("Primary Technician");

    if (!workToSave.property?.trim())
      missingFields.push("Property");

    if (!workToSave.address_line?.trim())
      missingFields.push("Street Address");

    if (!workToSave.city?.trim())
      missingFields.push("City");

    if (!workToSave.state?.trim())
      missingFields.push("State");

    if (!workToSave.zipcode?.trim())
      missingFields.push("Zipcode");

    if (missingFields.length > 0) {
      setToast({
        message: `⚠️ Please fill out: ${missingFields.join(", ")}`,
        type: "error",
      });
      return;
    }

    try {
      const token = await auth.currentUser.getIdToken();

      await axios.put(
        `${API_BASE_URL}/api/work/${workToSave.id}/`,
        workToSave,
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        }
      );

      setToast({ message: "✅ Work event updated", type: "success" });

      refresh();

      return true;
    } catch (err) {
      console.error(err);
      setToast({ message: "❌ Update failed", type: "error" });
    }
  };

  const handleSaveEventEdit = async (eventToSave) => {
    if (!eventToSave) return;

    const missingFields = [];

    if (!eventToSave.event_type) missingFields.push("Event Type");
    if (!eventToSave.title?.trim()) missingFields.push("Event Title");
    if (!eventToSave.date_and_time) missingFields.push("Date & Time");
    if (!eventToSave.primary_technician)
      missingFields.push("Primary Technician");

    if (missingFields.length > 0) {
      setToast({
        message: `⚠️ Please fill out: ${missingFields.join(", ")}`,
        type: "error",
      });
      return;
    }

    try {
      const token = await auth.currentUser.getIdToken();

      await axios.put(
        `${API_BASE_URL}/api/event/${eventToSave.id}/`,
        eventToSave,
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        }
      );

      setToast({ message: "✅ Event updated", type: "success" });

      setIsEditing(false);
      setIsModalOpen(false);
      setSelectedEvent(null);
      refresh();
    } catch (err) {
      console.error(err);
      setToast({ message: "❌ Update failed", type: "error" });
    }
  };

  const handleDeleteWorkEvent = async (onClose) => {
    if (!selectedEvent?.id) return;

    const confirmed = window.confirm(
      "Are you sure you want to delete this work event? This cannot be undone."
    );

    if (!confirmed) return;

    try {
      const token = await auth.currentUser.getIdToken(true);

      await axios.delete(
        `${API_BASE_URL}/api/work/${selectedEvent.id}/`,
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        }
      );

      setToast({ message: "🗑️ Work event deleted", type: "success" });

      onClose();

      // Close modal
      setIsModalOpen(false);
      setSelectedEvent(null);

      // Refresh calendar
      refresh();
    } catch (err) {
      console.error("Delete failed:", err);
      setToast({
        message: "❌ Failed to delete work event",
        type: "error",
      });
    }
  };


  const handleDeleteEvent = async () => {
    if (!selectedEvent?.id) return;

    const confirmed = window.confirm(
      "Are you sure you want to delete this event? This cannot be undone."
    );

    if (!confirmed) return;

    try {
      const token = await auth.currentUser.getIdToken(true);

      await axios.delete(
        `${API_BASE_URL}/api/event/${selectedEvent.id}/`,
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        }
      );

      setToast({ message: "🗑️ Event deleted", type: "success" });

      setIsModalOpen(false);
      setSelectedEvent(null);

      // Refresh calendar
      refresh();
    } catch (err) {
      console.error("Error deleting event:", err.response?.data || err.message);
      setToast({
        message: "❌ Failed to delete event",
        type: "error",
      });
    }
  };


  // === Sign Out ===
  const handleSignOut = async () => {
    try {
      await firebaseSignOut(auth);
      sessionStorage.clear();
      localStorage.clear();
      navigate("/");
    } catch (err) {
      console.error("Sign out error:", err);
    }
  };

  return {
    handleFileUpload,
    handleDeleteAll,
    handleSaveWorkEdit,
    handleSaveEventEdit,
    handleDeleteWorkEvent,
    handleDeleteEvent,
    handleSignOut,
  };
}
